import { useEffect, useState } from "react";
import { AlertTriangle, Ban, CheckCircle2, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import ConfirmDialog from "@/components/ConfirmDialog";
import { WRITE_ROLES, useAuth } from "@/contexts/AuthContext";
import { api, fmtBR, formatApiError, WEEKDAY_NAMES } from "@/lib/api";

const REQUEST_STATUS = {
  aguardando_professor: "Aguardando professor",
  aceito: "Aceito pelo professor",
  alternativo_sugerido: "Horário alternativo sugerido",
  sem_disponibilidade: "Professor sem disponibilidade",
  cancelado: "Cancelado pela administração",
  expirado: "Expirado",
};

const HISTORY_LABELS = {
  criacao: "Solicitação criada / encaminhamento",
  aceite: "Aceito pelo professor",
  sugestao: "Sugestão de horário",
  recusa: "Professor sem disponibilidade",
  cancelamento: "Cancelado pela administração",
  expiracao: "Expirado",
};

export const RequestStatusBadge = ({ status }) => (
  <Badge
    variant={status === "aceito" ? "default" : ["sem_disponibilidade", "cancelado", "expirado"].includes(status) ? "destructive" : "secondary"}
    className={status === "aceito" ? "bg-primary text-primary-foreground" : ""}
  >
    {REQUEST_STATUS[status] || status}
  </Badge>
);

const scheduleLabel = (r) =>
  r.weekday != null ? `${WEEKDAY_NAMES[r.weekday]} — ${r.start_time} às ${r.end_time}` : "—";

export default function Requests() {
  const { user } = useAuth();
  const isProfessor = user.role === "professor";
  const canManage = WRITE_ROLES.includes(user.role);
  const [requests, setRequests] = useState([]);
  const [lookups, setLookups] = useState({ teachers: [], courses: [] });
  const [filters, setFilters] = useState({ teacher_id: "todos", course_id: "todos", status: "todos", period_start: "", period_end: "" });
  const [detail, setDetail] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [suggestTarget, setSuggestTarget] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [suggestNote, setSuggestNote] = useState("");
  const [unavailableTarget, setUnavailableTarget] = useState(null);
  const [unavailableNote, setUnavailableNote] = useState("");
  const [actionError, setActionError] = useState("");

  const load = async () => {
    const params = {};
    if (!isProfessor) {
      if (filters.teacher_id !== "todos") params.teacher_id = filters.teacher_id;
      if (filters.course_id !== "todos") params.course_id = filters.course_id;
      if (filters.period_start) params.period_start = filters.period_start;
      if (filters.period_end) params.period_end = filters.period_end;
    }
    if (filters.status !== "todos") params.status = filters.status;
    const requests_ = [api.get("/requests", { params })];
    if (!isProfessor) requests_.push(api.get("/teachers"), api.get("/courses"));
    const [r, t, c] = await Promise.all(requests_);
    setRequests(r.data);
    if (!isProfessor) setLookups({ teachers: t.data, courses: c.data });
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.teacher_id, filters.course_id, filters.status, filters.period_start, filters.period_end]);

  const handleActionError = (err) => {
    const detail = err?.response?.data?.detail;
    if (detail?.message) {
      const conflicts = detail.conflicts ? ` ${detail.conflicts.join(" ")}` : "";
      return `${detail.message}${conflicts}`;
    }
    return formatApiError(err);
  };

  const accept = async (req) => {
    setActionError("");
    try {
      await api.post(`/requests/${req.id}/accept`);
      toast.success("Horário aceito.");
      load();
    } catch (err) {
      toast.error(handleActionError(err));
      load();
    }
  };

  const openSuggest = async (req) => {
    setActionError("");
    setSelectedSlot("");
    setSuggestNote("");
    try {
      const { data } = await api.get(`/requests/${req.id}/valid-slots`);
      setSlots(data);
      setSuggestTarget(req);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submitSuggest = async () => {
    if (!selectedSlot) return;
    const [weekday, start_time] = selectedSlot.split("|");
    try {
      await api.post(`/requests/${suggestTarget.id}/suggest`, {
        weekday: Number(weekday), start_time, teacher_response: suggestNote || undefined,
      });
      toast.success("Horário alternativo sugerido.");
      setSuggestTarget(null);
      load();
    } catch (err) {
      setActionError(handleActionError(err));
    }
  };

  const submitUnavailable = async () => {
    try {
      await api.post(`/requests/${unavailableTarget.id}/unavailable`, {
        teacher_response: unavailableNote || undefined,
      });
      toast.success("Resposta registrada.");
      setUnavailableTarget(null);
      setUnavailableNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancel = async () => {
    try {
      await api.post(`/requests/${cancelling.id}/cancel`);
      toast.success("Solicitação cancelada.");
      setCancelling(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setCancelling(null);
    }
  };

  const openDetail = async (req) => {
    try {
      const { data } = await api.get(`/requests/${req.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pending = requests.filter((r) => r.status === "aguardando_professor");
  const answered = requests.filter((r) => r.status !== "aguardando_professor");

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Solicitações</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {isProfessor
            ? "Interessados encaminhados a você pela administração."
            : "Encaminhamentos de interessados aos professores e respectivas respostas."}
        </p>
      </div>

      {!isProfessor && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Select value={filters.teacher_id} onValueChange={(v) => setFilters({ ...filters, teacher_id: v })}>
            <SelectTrigger data-testid="request-filter-teacher"><SelectValue placeholder="Professor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os professores</SelectItem>
              {lookups.teachers.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.course_id} onValueChange={(v) => setFilters({ ...filters, course_id: v })}>
            <SelectTrigger data-testid="request-filter-course"><SelectValue placeholder="Curso" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os cursos</SelectItem>
              {lookups.courses.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="request-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos os status</SelectItem>
              {Object.entries(REQUEST_STATUS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={filters.period_start} onChange={(e) => setFilters({ ...filters, period_start: e.target.value })} data-testid="request-filter-period-start" title="Encaminhado a partir de" />
          <Input type="date" value={filters.period_end} onChange={(e) => setFilters({ ...filters, period_end: e.target.value })} data-testid="request-filter-period-end" title="Encaminhado até" />
        </div>
      )}

      {isProfessor ? (
        <>
          <section className="space-y-3">
            <h2 className="font-heading text-lg font-semibold">Aguardando sua resposta</h2>
            {pending.length === 0 && (
              <p className="border border-border bg-white p-6 text-sm text-muted-foreground" data-testid="requests-pending-empty">
                Nenhuma solicitação pendente.
              </p>
            )}
            {pending.map((r) => (
              <div key={r.id} className="border border-border border-l-4 border-l-primary bg-white p-4" data-testid={`request-card-${r.id}`}>
                <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">Novo interessado</Badge>
                <p className="mt-2 font-heading text-xl font-bold">{r.lead_name}</p>
                <p className="text-sm text-muted-foreground">{r.lead_age} anos</p>
                <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{r.course_name}</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Modalidade</p><p className="font-medium">{r.modality_name} ({r.duration_minutes} min)</p></div>
                  <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário solicitado</p><p className="font-medium" data-testid={`request-schedule-${r.id}`}>{scheduleLabel(r)}</p></div>
                </div>
                <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                  <Button onClick={() => accept(r)} className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid={`request-accept-${r.id}`}>
                    <CheckCircle2 size={16} className="mr-2" /> Aceitar
                  </Button>
                  <Button variant="outline" onClick={() => openSuggest(r)} data-testid={`request-suggest-${r.id}`}>
                    <Clock size={16} className="mr-2" /> Sugerir outro horário
                  </Button>
                  <Button variant="outline" onClick={() => { setUnavailableTarget(r); setUnavailableNote(""); }} className="border-destructive/50 text-destructive transition-colors hover:bg-destructive/10" data-testid={`request-unavailable-${r.id}`}>
                    <Ban size={16} className="mr-2" /> Não tenho disponibilidade
                  </Button>
                </div>
              </div>
            ))}
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-lg font-semibold">Respondidas</h2>
            {answered.length === 0 && (
              <p className="border border-border bg-white p-6 text-sm text-muted-foreground" data-testid="requests-answered-empty">
                Nenhuma solicitação respondida.
              </p>
            )}
            {answered.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 border border-border bg-white p-4" data-testid={`request-answered-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{r.lead_name} ({r.lead_age} anos) · {r.course_name}</p>
                  <p className="text-xs text-muted-foreground">{scheduleLabel(r)}</p>
                  {r.suggested_schedule && (
                    <p className="mt-1 text-xs font-medium text-primary">Sugerido: {r.suggested_schedule.label}</p>
                  )}
                </div>
                <RequestStatusBadge status={r.status} />
              </div>
            ))}
          </section>
        </>
      ) : (
        <div className="border border-border bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider">Interessado</TableHead>
                <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Curso</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Horário</TableHead>
                <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Professor</TableHead>
                <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Resposta</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Data</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="p-6 text-sm text-muted-foreground" data-testid="requests-empty">
                    Nenhuma solicitação encontrada. Encaminhe um interessado pela página Interessados.
                  </TableCell>
                </TableRow>
              )}
              {requests.map((r) => (
                <TableRow key={r.id} className="transition-colors hover:bg-zinc-50" data-testid={`request-row-${r.id}`}>
                  <TableCell className="font-medium">
                    {r.lead_name}
                    {r.lead_age != null && <span className="ml-1 text-xs text-muted-foreground">({r.lead_age} anos)</span>}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{r.course_name} · {r.duration_minutes} min</TableCell>
                  <TableCell>{scheduleLabel(r)}</TableCell>
                  <TableCell className="hidden md:table-cell">{r.teacher_name}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {r.suggested_schedule ? (
                      <span className="text-xs font-medium text-primary">{r.suggested_schedule.label}</span>
                    ) : r.teacher_response ? (
                      <span className="text-xs">{r.teacher_response}</span>
                    ) : "—"}
                  </TableCell>
                  <TableCell><RequestStatusBadge status={r.status} /></TableCell>
                  <TableCell className="hidden lg:table-cell">{fmtBR((r.forwarded_at || "").slice(0, 10))}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(r)} data-testid={`request-view-${r.id}`}>
                      <Eye size={16} />
                    </Button>
                    {canManage && r.status === "aguardando_professor" && (
                      <Button variant="ghost" size="icon" title="Cancelar solicitação" onClick={() => setCancelling(r)} data-testid={`request-cancel-${r.id}`}>
                        <Ban size={16} className="text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!suggestTarget} onOpenChange={() => setSuggestTarget(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="suggest-dialog">
          <DialogHeader>
            <DialogTitle>Sugerir outro horário — {suggestTarget?.lead_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {actionError && (
              <p className="border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive" data-testid="suggest-error">{actionError}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Somente horários compatíveis com sua disponibilidade, duração da modalidade ({suggestTarget?.duration_minutes} min) e sua agenda são exibidos.
            </p>
            {slots.length === 0 ? (
              <p className="border border-border bg-zinc-50 p-4 text-sm" data-testid="suggest-no-slots">
                Nenhum horário válido disponível no momento.
              </p>
            ) : (
              <RadioGroup value={selectedSlot} onValueChange={setSelectedSlot} className="max-h-64 space-y-1 overflow-y-auto" data-testid="suggest-slots-group">
                {slots.map((s) => (
                  <label key={`${s.weekday}-${s.start_time}`} className="flex cursor-pointer items-center gap-3 border border-border p-3 text-sm transition-colors hover:bg-zinc-50" data-testid={`suggest-slot-${s.weekday}-${s.start_time}`}>
                    <RadioGroupItem value={`${s.weekday}|${s.start_time}`} />
                    {s.label}
                  </label>
                ))}
              </RadioGroup>
            )}
            <div className="space-y-2">
              <Label>Observação (opcional)</Label>
              <Textarea rows={2} value={suggestNote} onChange={(e) => setSuggestNote(e.target.value)} data-testid="suggest-note-input" />
            </div>
            <Button
              onClick={submitSuggest}
              disabled={!selectedSlot}
              className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="suggest-submit-button"
            >
              Enviar sugestão
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!unavailableTarget} onOpenChange={() => setUnavailableTarget(null)}>
        <DialogContent data-testid="unavailable-dialog">
          <DialogHeader>
            <DialogTitle>Sem disponibilidade — {unavailableTarget?.lead_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A administração será informada de que você não possui disponibilidade para este horário.
            </p>
            <div className="space-y-2">
              <Label>Observação (opcional)</Label>
              <Textarea rows={2} value={unavailableNote} onChange={(e) => setUnavailableNote(e.target.value)} data-testid="unavailable-note-input" />
            </div>
            <Button onClick={submitUnavailable} className="w-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90" data-testid="unavailable-submit-button">
              Confirmar resposta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle>Solicitação — {detail?.lead_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{detail.course_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Modalidade</p><p className="font-medium">{detail.modality_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário</p><p className="font-medium">{scheduleLabel(detail)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Professor</p><p className="font-medium">{detail.teacher_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><RequestStatusBadge status={detail.status} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Encaminhado por</p><p className="font-medium">{detail.forwarded_by_name || "—"}</p></div>
                {detail.suggested_schedule && (
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Horário alternativo</p><p className="font-medium text-primary">{detail.suggested_schedule.label}</p></div>
                )}
                {detail.teacher_response && (
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Resposta do professor</p><p className="font-medium">{detail.teacher_response}</p></div>
                )}
                {detail.admin_note && (
                  <div className="col-span-2"><p className="text-xs uppercase tracking-wider text-muted-foreground">Observação interna</p><p className="font-medium">{detail.admin_note}</p></div>
                )}
              </div>
              {detail.lead && (
                <div>
                  <h3 className="font-heading text-base font-semibold">Dados do interessado</h3>
                  <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Idade</p><p className="font-medium">{detail.lead_age} anos</p></div>
                    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">WhatsApp</p><p className="font-medium">{detail.lead.whatsapp}</p></div>
                    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">E-mail</p><p className="font-medium">{detail.lead.email || "—"}</p></div>
                  </div>
                </div>
              )}
              {(detail.history || []).length > 0 && (
                <div>
                  <h3 className="font-heading text-base font-semibold">Histórico</h3>
                  <div className="mt-2 space-y-2" data-testid="request-history-list">
                    {detail.history.map((h) => (
                      <div key={h.id} className="border border-border p-2 text-xs">
                        <span className="font-semibold">{HISTORY_LABELS[h.action] || h.action}</span>
                        <span className="text-muted-foreground"> · {h.user_name} · {new Date(h.created_at).toLocaleString("pt-BR")}</span>
                        {h.changes?.suggested && <p className="mt-1 text-muted-foreground">Sugerido: {h.changes.suggested}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!cancelling}
        onOpenChange={() => setCancelling(null)}
        title="Cancelar solicitação"
        description={`Cancelar o encaminhamento de "${cancelling?.lead_name}" para ${cancelling?.teacher_name}? O interessado voltará para análise.`}
        onConfirm={cancel}
        testId="request-cancel-confirm"
      />
    </div>
  );
}
